import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasCrisisKeyword, beliefQuote } from "@/lib/gatewayContent";
import { CrisisDialog } from "@/components/Crisis";
import SoundSanctuary from "@/components/gateway/SoundSanctuary";
import SanctuaryHeader from "@/components/gateway/SanctuaryHeader";
import { Mic, MicOff, Flame, Waves, X, Crown, Lock, Moon } from "lucide-react";
import { toast } from "sonner";

const Chaos = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [dissolving, setDissolving] = useState(false);
  const [listening, setListening] = useState(false);
  const [emotion, setEmotion] = useState(null);
  const [crisis, setCrisis] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quote] = useState(() => beliefQuote(user?.belief_filter));
  const [usage, setUsage] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showGuest, setShowGuest] = useState(false);
  const recRef = useRef(null);

  const loadUsage = () => {
    if (!user) return;
    api.get("/usage/reflections").then(({ data }) => setUsage(data)).catch(() => {});
  };
  useEffect(() => { loadUsage(); }, [user]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const r = new SR();
      r.continuous = true; r.interimResults = true; r.lang = "en-US";
      r.onresult = (e) => {
        let final = "";
        for (let i = e.resultIndex; i < e.results.length; i++) if (e.results[i].isFinal) final += e.results[i][0].transcript;
        if (final) setText((t) => (t ? t + " " : "") + final.trim());
      };
      r.onend = () => setListening(false);
      recRef.current = r;
    }
    return () => { try { recRef.current && recRef.current.stop(); } catch { /* noop */ } };
  }, []);

  const toggleMic = () => {
    if (!recRef.current) { toast.info("Voice input isn't supported in this browser."); return; }
    if (listening) { recRef.current.stop(); setListening(false); }
    else { try { recRef.current.start(); setListening(true); } catch { /* noop */ } }
  };

  const dissolve = () => {
    if (!text) return;
    setDissolving(true);
    setTimeout(() => { setText(""); setDissolving(false); setEmotion(null); }, 1200);
  };

  const submit = async () => {
    if (!text.trim()) return;
    // Hardcoded priority crisis monitor — bypasses all AI and any account gate
    if (hasCrisisKeyword(text)) { setCrisis(true); return; }
    // Guests can vent freely, but saving to the private database requires an account + consent
    if (!user) { setShowGuest(true); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/journal", { title: "Mind dump", content: text });
      if (data.crisis) { setCrisis(true); return; }
      const emo = (data.analysis?.primary_emotions || [])[0] || "what you're carrying";
      setEmotion(emo);
      loadUsage();
    } catch (err) {
      if (err.response?.status === 402) { setShowUpgrade(true); return; }
      setEmotion("what you're carrying");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-5 py-6 relative"
      style={{ background: "radial-gradient(ellipse at 50% 20%, #241f2e 0%, #14121b 60%, #0c0a10 100%)" }}
      data-testid="chaos-screen">
      <SanctuaryHeader tone="dark" className="absolute top-5 left-5 z-20" />
      <button onClick={() => navigate("/gateway")} data-testid="chaos-exit" className="absolute top-5 right-5 text-white/25 hover:text-white/60 z-10"><X className="w-6 h-6" /></button>

      <p className="text-white/50 font-light text-center mb-1 mt-6">Let it all out. Nothing here is judged.</p>
      {user && usage && !usage.unlimited && (
        <p className="text-center text-white/30 text-xs mb-3" data-testid="reflection-tracker">
          {usage.remaining} of {usage.limit} daily reflections remaining
        </p>
      )}
      {(!user || (usage && usage.unlimited)) && <div className="mb-3" />}

      {!emotion && (
        <>
          <div className="relative flex-1 min-h-[45vh]">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Pour your mind onto the page…"
              data-testid="mind-dump-canvas"
              className={`w-full h-full bg-transparent text-white/85 text-lg leading-relaxed resize-none outline-none placeholder:text-white/25 transition-all duration-1000 ${dissolving ? "opacity-0 blur-md scale-95" : "opacity-100"}`}
            />
            {dissolving && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><Flame className="w-10 h-10 text-orange-400/70 animate-ping" /></div>}
          </div>

          <div className="flex items-center justify-between gap-3 mt-4">
            <button onClick={toggleMic} data-testid="mic-button"
              className={`flex items-center gap-2 rounded-full px-4 py-3 border transition-colors ${listening ? "bg-red-400/20 border-red-300/40 text-white" : "bg-white/5 border-white/15 text-white/70 hover:text-white"}`}>
              {listening ? <Mic className="w-5 h-5 animate-pulse" /> : <MicOff className="w-5 h-5" />}
              {listening ? "Listening…" : "Voice"}
            </button>
            <div className="flex gap-3">
              <button onClick={dissolve} data-testid="dissolve-button"
                className="flex items-center gap-2 rounded-full px-5 py-3 border border-orange-300/30 bg-orange-400/10 text-orange-200 hover:bg-orange-400/20 transition-colors">
                <Flame className="w-4 h-4" /> Dissolve
              </button>
              <button onClick={submit} disabled={submitting || !text.trim()} data-testid="chaos-submit"
                className="flex items-center gap-2 rounded-full px-5 py-3 border border-teal-200/30 bg-teal-200/15 text-white hover:bg-teal-200/25 transition-colors disabled:opacity-40">
                <Waves className="w-4 h-4" /> {submitting ? "Attuning…" : "Sound Sanctuary"}
              </button>
            </div>
          </div>
        </>
      )}

      {emotion && (
        <div className="flex-1 flex flex-col justify-center max-w-lg mx-auto w-full animate-fade-up">
          <SoundSanctuary emotion={emotion} />
          <button onClick={() => setEmotion(null)} className="text-white/40 hover:text-white/70 text-sm mt-6 mx-auto" data-testid="chaos-back">Back to the canvas</button>
        </div>
      )}

      <p className="text-center text-white/20 text-xs mt-4 font-light">{quote}</p>
      <CrisisDialog open={crisis} onOpenChange={setCrisis} />

      {/* Empathetic daily-limit intercept */}
      {showUpgrade && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-6" data-testid="upgrade-intercept">
          <div className="absolute inset-0 bg-[#070d11]/85 backdrop-blur-md" onClick={() => setShowUpgrade(false)} />
          <div className="relative w-full max-w-sm rounded-3xl border border-white/10 bg-[#0f1a20]/90 backdrop-blur-2xl p-7 text-center">
            <span className="w-14 h-14 rounded-full bg-teal-200/10 flex items-center justify-center mx-auto mb-4">
              <Moon className="w-6 h-6 text-teal-200/80" />
            </span>
            <h3 className="font-serif text-2xl text-white">You've poured out a lot today</h3>
            <p className="text-white/55 text-sm mt-3 leading-relaxed">
              Fifteen reflections, all held with care. Your mind has done enough unloading for now — let it rest.
              Come back tomorrow, or open unlimited space with Aurea Plus.
            </p>
            <div className="mt-6 flex flex-col gap-2.5">
              <button onClick={() => navigate("/app/plus")} data-testid="intercept-upgrade"
                className="rounded-full py-3 bg-teal-200/15 border border-teal-200/30 text-white hover:bg-teal-200/25 transition-colors flex items-center justify-center gap-2">
                <Crown className="w-4 h-4" /> Unlock unlimited with Aurea Plus
              </button>
              <button onClick={() => setShowUpgrade(false)} data-testid="intercept-rest"
                className="rounded-full py-3 text-white/50 hover:text-white/80 transition-colors">
                I'll rest for now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guest registration + consent gate */}
      {showGuest && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-6" data-testid="guest-gate">
          <div className="absolute inset-0 bg-[#070d11]/85 backdrop-blur-md" onClick={() => setShowGuest(false)} />
          <div className="relative w-full max-w-sm rounded-3xl border border-white/10 bg-[#0f1a20]/90 backdrop-blur-2xl p-7 text-center">
            <span className="w-14 h-14 rounded-full bg-teal-200/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-teal-200/80" />
            </span>
            <h3 className="font-serif text-2xl text-white">Keep this safe & private</h3>
            <p className="text-white/55 text-sm mt-3 leading-relaxed">
              To save your mind dump into your own encrypted, private space — and keep your safety net active —
              create a free account. It takes a moment, and you'll agree to our simple Terms &amp; Privacy along the way.
            </p>
            <div className="mt-6 flex flex-col gap-2.5">
              <button onClick={() => navigate("/register")} data-testid="guest-register"
                className="rounded-full py-3 bg-teal-200/15 border border-teal-200/30 text-white hover:bg-teal-200/25 transition-colors">
                Create your sanctuary
              </button>
              <button onClick={() => setShowGuest(false)} data-testid="guest-keep-venting"
                className="rounded-full py-3 text-white/50 hover:text-white/80 transition-colors">
                Keep venting (not saved)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chaos;
